import { useEffect, useState, lazy, Suspense } from 'react';
import { Instagram, Linkedin, Copy, Check } from 'lucide-react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import ReactGA from "react-ga4";
import { useAuthStore } from '@/stores/authStore';
import { Button } from "@/components/ui/button";
import mattImage from '../assets/images/matt.webp';
import derekImage from '../assets/images/derek.webp';
import newLogo from '../assets/images/new-logo.webp';
import artieImage from '../assets/images/base.webp';

const EarlyAccessDialog = lazy(() => import('@/components/EarlyAccessDialog'));

const LandingPage = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isEarlyAccessOpen, setIsEarlyAccessOpen] = useState(false);
  const [hoveredParagraph, setHoveredParagraph] = useState<number | null>(null);
  const { isAuthenticated, user } = useAuthStore();
  const navigate = useNavigate();

  // Helper function to determine dashboard route
  const getDashboardRoute = () => {
    console.log('🔍 LandingPage getDashboardRoute called');
    console.log('🔍 user object:', user);
    console.log('🔍 user role:', user?.role);
    console.log('🔍 isAuthenticated:', isAuthenticated);
    console.log('🔍 is brand user check:', user?.role === 'brand_user');
    
    // Check for brand user role
    if (user?.role === 'brand_user') {
      console.log('✅ Returning /brand-dashboard');
      return '/brand-dashboard';
    }
    
    console.log('❌ Returning /dashboard');
    return '/dashboard';
  };
  const [searchParams] = useSearchParams();

  // Check for referral code in URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      sessionStorage.setItem("referral_code", refCode);
    }
  }, [searchParams]);

  // COMMENTED OUT: YouTube OAuth flow
  // useEffect(() => {
  //   const earlyAccessData = sessionStorage.getItem("early_access_youtube_data");
  //   if (earlyAccessData) {
  //     try {
  //       const data = JSON.parse(earlyAccessData);
  //       setYoutubeData(data);
  //       setIsYouTubeConnected(true);
  //       // Pre-fill fields if we have them and they're empty (optional)
  //       if (data.title) setName(data.title); // Usually channel name matches creator name or alias
  //
  //       setIsEarlyAccessOpen(true);
  //       setStep(2);
  //       setSelectedUserType('creator');
  //
  //       sessionStorage.removeItem("early_access_youtube_data"); // Consume it
  //     } catch (e) {
  //       console.error("Failed to parse early access youtube data", e);
  //     }
  //   }
  // }, []);

  // const handleConnectYouTube = async () => {
  //   setIsConnectingYoutube(true);
  //   try {
  //       // Set return path to root (landing page) to signal early access flow
  //       localStorage.setItem("oauth_return_path", "/");
  //       const { url } = await apiClient.connectEarlyAccessYouTube();
  //       window.location.href = url;
  //   } catch (error) {
  //       console.error(error);
  //       toast.error("Failed to initiate YouTube connection");
  //       setIsConnectingYoutube(false);
  //   }
  // };


  useEffect(() => {
    document.title = "Artie - The Creator's Companion";
  }, []);

  return (
    <div className="antialiased bg-white text-neutral-900 font-geist relative isolate">

      {/* Navigation (Sticky) */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-neutral-100 transition-all duration-300">
        <div className="lg:px-8 max-w-7xl mr-auto ml-auto pr-6 pl-6">
          <div className="flex h-20 items-center justify-between">
            {/* Brand */}
            <Link to="/" className="flex items-center gap-2 group">
              <img src={newLogo} alt="Artie" className="w-40 h-40 object-contain" width="160" height="160" />
            </Link>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-4">
              {!isAuthenticated ? (
                <>
                  <Link to="/login">
                    <button onClick={() => {
                      ReactGA.event({ category: "Landing Page", action: "Click Login", label: "Header Desktop" });
                    }} className="inline-flex items-center justify-center h-10 px-5 text-sm font-medium text-neutral-900 transition-colors bg-white border border-neutral-300 rounded-full hover:bg-neutral-50 font-geist shadow-md hover:shadow-lg">Login</button>
                  </Link>
                  <Link to="/signup">
                    <button onClick={() => {
                      ReactGA.event({ category: "Landing Page", action: "Click Signup", label: "Header Desktop" });
                    }} className="inline-flex items-center justify-center h-10 px-5 text-sm font-medium text-white transition-colors bg-neutral-900 rounded-full hover:bg-neutral-800 font-geist shadow-md hover:shadow-lg">Sign Up</button>
                  </Link>
                </>
              ) : (
                <div>
                  <button onClick={() => {
                    ReactGA.event({ category: "Landing Page", action: "Go to Dashboard", label: "Header Desktop" });
                    const route = getDashboardRoute();
                    navigate(route);
                  }} className="inline-flex items-center justify-center h-10 px-5 text-sm font-medium text-white transition-colors bg-neutral-900 rounded-full hover:bg-neutral-800 font-geist shadow-md hover:shadow-lg">Dashboard</button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden inline-flex hover:bg-neutral-100 transition-colors bg-neutral-50 w-10 h-10 rounded-full items-center justify-center ring-1 ring-black/5"
              aria-label="Toggle menu"
              onClick={() => {
                ReactGA.event({ category: "Landing Page", action: "Toggle Mobile Menu" });
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
            >
              {!isMobileMenuOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-neutral-900">
                  <path d="M3 12h18"></path>
                  <path d="M3 6h18"></path>
                  <path d="M3 18h18"></path>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-neutral-900">
                  <path d="M18 6 6 18"></path>
                  <path d="M6 6l12 12"></path>
                </svg>
              )}
            </button>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-neutral-100 shadow-xl z-50">
              <nav className="p-6 space-y-4">
                <div className="pt-4 border-t border-neutral-100 flex flex-col gap-3">
                  {!isAuthenticated ? (
                    <>
                      <Link to="/login">
                        <button onClick={() => {
                          ReactGA.event({ category: "Landing Page", action: "Click Login", label: "Mobile Menu" });
                          setIsMobileMenuOpen(false);
                        }} className="flex items-center justify-center w-full h-12 text-base font-medium text-neutral-900 bg-white border border-neutral-300 rounded-full shadow-md font-geist">Login</button>
                      </Link>
                      <Link to="/signup">
                        <button onClick={() => {
                          ReactGA.event({ category: "Landing Page", action: "Click Signup", label: "Mobile Menu" });
                          setIsMobileMenuOpen(false);
                        }} className="flex items-center justify-center w-full h-12 text-base font-medium text-white bg-neutral-900 rounded-full shadow-md font-geist">Sign Up</button>
                      </Link>
                    </>
                  ) : (
                    <button onClick={() => {
                      ReactGA.event({ category: "Landing Page", action: "Go to Dashboard", label: "Mobile Menu" });
                      setIsMobileMenuOpen(false);
                      const route = getDashboardRoute();
                      navigate(route);
                    }} className="flex items-center justify-center w-full h-12 text-base font-medium text-white bg-neutral-900 rounded-full shadow-md font-geist">Dashboard</button>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      <main className="">
        {/* Hero Section */}
        <section className="sm:px-8 max-w-7xl mx-auto px-6 min-h-[calc(100vh-5rem)] flex items-center pb-16 pt-10 lg:pt-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center w-full">
            {/* Left: Copy */}
            <div className="max-w-xl relative z-10">

              {/* Headline */}
              <span className="block text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tighter font-geist leading-[1.05] mb-4">Artie</span>
              <h1 className="sm:text-5xl lg:text-[64px] leading-[1.05] transition-all duration-700 ease-out delay-150 text-4xl font-semibold text-neutral-900 tracking-tighter font-geist mb-6">
                The Creator's Companion
              </h1>

              <p className="text-lg sm:text-xl leading-relaxed text-neutral-600 font-geist mb-8 transition-all duration-700 ease-out delay-300 max-w-md">
                Commercial infrastructure for independent creators.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-12 transition-all duration-700 ease-out delay-[450ms]">
                {isAuthenticated && user ? (
                  <>
                    <div className="w-full sm:w-auto">
                      <Button onClick={() => {
                        ReactGA.event({ category: "Landing Page", action: "Go to Dashboard", label: "Hero (Auth)" });
                        const route = getDashboardRoute();
                        navigate(route);
                      }} className="group inline-flex items-center justify-center hover:bg-neutral-800 transition-colors font-medium text-white bg-neutral-900 rounded-full h-12 px-8 shadow-lg hover:shadow-xl w-full">
                        <span className="font-geist">Go to Dashboard</span>
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                      <Link to="/login" className="w-full sm:w-auto">
                        <button onClick={() => {
                          ReactGA.event({ category: "Landing Page", action: "Click Login", label: "Hero (Guest)" });
                        }} className="group inline-flex items-center justify-center hover:bg-neutral-50 transition-colors font-medium text-neutral-900 bg-white border border-neutral-300 rounded-full h-12 px-8 shadow-lg hover:shadow-xl w-full">
                          <span className="font-geist">Login</span>
                        </button>
                      </Link>
                      <Link to="/signup" className="w-full sm:w-auto">
                        <button onClick={() => {
                          ReactGA.event({ category: "Landing Page", action: "Click Signup", label: "Hero (Guest)" });
                        }} className="group inline-flex items-center justify-center hover:bg-neutral-800 transition-colors font-medium text-white bg-neutral-900 rounded-full h-12 px-8 shadow-lg hover:shadow-xl w-full">
                          <span className="font-geist">Sign Up</span>
                        </button>
                      </Link>
                    </div>
                  </>
                )}
              </div>

            </div>

            {/* Right: Artie Image */}
            <div className="relative w-full flex items-center justify-center select-none">
              <img
                src={artieImage}
                alt="Artie Dashboard"
                className="w-full h-auto max-h-[700px] object-contain"
                width="700"
                height="700"
              />
            </div>
          </div>
        </section>

        {/* What is Artie Section */}
        <section className="py-24 bg-white">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 font-geist mb-10 tracking-wide uppercase">
              What is Artie?
            </h2>
            <div
              className="space-y-12 text-3xl sm:text-5xl font-bold text-neutral-900 leading-tight font-geist tracking-tight"
              onMouseLeave={() => setHoveredParagraph(null)}
            >
              <p
                className={`transition-colors duration-300 cursor-default ${hoveredParagraph === 2 ? 'text-neutral-500' : 'text-neutral-900'}`}
                onMouseEnter={() => setHoveredParagraph(1)}
              >
                As traditional studio models shift, creators with big ideas often lack the support structures to fully realize their potential.
              </p>
              <p
                className={`transition-colors duration-300 cursor-default ${hoveredParagraph === 2 ? 'text-neutral-900' : 'text-neutral-500'}`}
                onMouseEnter={() => setHoveredParagraph(2)}
              >
                Artie bridges the gap between creative vision and financial reality. We provide an intelligence layer that gives brands, producers, and capital partners the confidence to back creators at the scale they deserve.
              </p>
            </div>
          </div>
        </section>

        {/* Who is Artie For Section */}
        <section className="bg-[#F7F7F7] border-y border-neutral-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 font-geist mb-4">
                Who is Artie for?
              </h2>
              <p className="text-xl text-neutral-600 font-geist">
                Designed for the people who bring stories to life
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Card 1: Creators */}
              <div className="bg-white p-8 rounded-3xl border border-neutral-100 shadow-sm">
                <h3 className="text-2xl font-bold text-neutral-900 font-geist mb-4">Creators</h3>
                <p className="text-xl text-neutral-600 font-geist">
                  Artie helps creators get more projects made — with tools to develop and package their work, and pathways to the capital that brings it to life.
                </p>
              </div>

              {/* Card 2: Capital Partners */}
              <div className="bg-white p-8 rounded-3xl border border-neutral-100 shadow-sm">
                <h3 className="text-2xl font-bold text-neutral-900 font-geist mb-4">Capital Partners</h3>
                <p className="text-xl text-neutral-600 font-geist">
                  Artie enables private credit for creators by transforming content and audience data into underwritable signals. Our model gives partners the confidence to assess risk and deploy capital across a new asset class.
                </p>
              </div>

              {/* Card 3: Brands */}
              <div className="bg-white p-8 rounded-3xl border border-neutral-100 shadow-sm">
                <h3 className="text-2xl font-bold text-neutral-900 font-geist mb-4">Brands</h3>
                <p className="text-xl text-neutral-600 font-geist">
                  Artie provides access to a new pipeline of content, gets brands further upstream, and helps identify the best fit balancing timing, scale and alignment needs - stepchanging predictability of critical content investments
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-32 bg-[#F7F7F7] border-b border-neutral-200" id="how-it-works">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">

              {/* Left Column (Sidebar) - approx 1/3 width */}
              <div className="lg:col-span-4 flex flex-col justify-center">
                <span className="text-sm font-bold text-neutral-500 uppercase tracking-wider font-geist mb-4">
                  How It Works
                </span>
                <h2 className="text-4xl sm:text-5xl lg:text-[42px] leading-[1.05] font-bold text-neutral-900 font-geist tracking-tight">
                  Turning ideas into viable projects
                </h2>
              </div>

              {/* Right Column (Content Grid) - approx 2/3 width */}
              <div className="lg:col-span-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

                  {/* Card 1 */}
                  <div className="bg-white rounded-[24px] p-8 border border-neutral-200 hover:shadow-lg transition-all duration-300 group h-full">
                    <span className="block text-sm font-bold text-neutral-400 font-geist mb-4">01</span>
                    <h3 className="text-xl font-bold text-neutral-900 font-geist mb-3">Create</h3>
                    <p className="text-neutral-600 leading-relaxed font-geist">
                      Optimize projects through development and packaging tools.
                    </p>
                  </div>

                  {/* Card 2 */}
                  <div className="bg-white rounded-[24px] p-8 border border-neutral-200 hover:shadow-lg transition-all duration-300 group h-full">
                    <span className="block text-sm font-bold text-neutral-400 font-geist mb-4">02</span>
                    <h3 className="text-xl font-bold text-neutral-900 font-geist mb-3">Score</h3>
                    <p className="text-neutral-600 leading-relaxed font-geist">
                      A proprietary engine generates robust predictions of audience reach and commercial value.
                    </p>
                  </div>

                  {/* Card 3 */}
                  <div className="bg-white rounded-[24px] p-8 border border-neutral-200 hover:shadow-lg transition-all duration-300 group h-full">
                    <span className="block text-sm font-bold text-neutral-400 font-geist mb-4">03</span>
                    <h3 className="text-xl font-bold text-neutral-900 font-geist mb-3">Match</h3>
                    <p className="text-neutral-600 leading-relaxed font-geist">
                      High-fidelity pairing between projects with aligned brands and capital partners.
                    </p>
                  </div>

                  {/* Card 4 */}
                  <div className="bg-white rounded-[24px] p-8 border border-neutral-200 hover:shadow-lg transition-all duration-300 group h-full">
                    <span className="block text-sm font-bold text-neutral-400 font-geist mb-4">04</span>
                    <h3 className="text-xl font-bold text-neutral-900 font-geist mb-3">Verify</h3>
                    <p className="text-neutral-600 leading-relaxed font-geist">
                      Track deliverables and ensure both sides are treated fairly—and fast!
                    </p>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Meet the Team Section */}
        <section className="py-24 bg-white" id="team">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Header */}
            <div className="text-center max-w-3xl mx-auto mb-20">
              <span className="text-sm font-bold text-neutral-500 uppercase tracking-wider font-geist mb-4 block">
                Meet the Team
              </span>
              <h2 className="text-4xl sm:text-5xl font-bold text-neutral-900 font-geist tracking-tight leading-tight">
                Built by people who know the ecosystem
              </h2>
            </div>

            {/* Profiles */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 max-w-5xl mx-auto">

              {/* Matt McDonald */}
              <div className="flex flex-col items-center text-center">
                <div className="w-48 h-48 rounded-full overflow-hidden mb-6 border-2 border-neutral-100 shadow-sm">
                  <img
                    src={mattImage}
                    alt="Matt McDonald"
                    className="w-full h-full object-cover"
                    width="192"
                    height="192"
                    loading="lazy"
                  />
                </div>
                <h3 className="text-2xl font-bold text-neutral-900 font-geist mb-2">Matt McDonald</h3>
                <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wider font-geist mb-4">CEO & CO-FOUNDER</p>
                <p className="text-neutral-600 leading-relaxed font-geist max-w-sm">
                  Matt McDonald is CEO of Artie and a former executive at YouTube and Spotify. A founding team member behind YouTube Kids, YouTube Music, and YouTube TV, Matt brings deep expertise in modern media economics and technology. He's also dog dad to the real-life Artie, whose curiosity and uniqueness inspired the company's name.
                </p>
              </div>

              {/* Derek Scobie */}
              <div className="flex flex-col items-center text-center">
                <div className="w-48 h-48 rounded-full overflow-hidden mb-6 border-2 border-neutral-100 shadow-sm">
                  <img
                    src={derekImage}
                    alt="Derek Scobie"
                    className="w-full h-full object-cover"
                    width="192"
                    height="192"
                    loading="lazy"
                  />
                </div>
                <h3 className="text-2xl font-bold text-neutral-900 font-geist mb-2">Derek Scobie</h3>
                <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wider font-geist mb-4">CCO & CO-FOUNDER</p>
                <p className="text-neutral-600 leading-relaxed font-geist max-w-sm">
                  Derek has built multiple billion-dollar businesses across the consumer goods, entertainment, and technology industries. A global champion for the sustained growth of the Creator Economy, he spent ~15 years at Google and YouTube. He’s a dog dad to Pepper, Bea, and Alfie.
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* Get Started Section */}
        <section className="py-32 bg-[#F7F7F7]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl sm:text-5xl font-bold text-neutral-900 font-geist tracking-tight mb-6">
              Get started today.
            </h2>
            <p className="text-neutral-600 text-lg sm:text-xl font-geist max-w-md mx-auto mb-10 leading-relaxed">
              Join Artie and start building your creative future.
            </p>
            {!isAuthenticated ? (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
                <Link to="/login">
                  <button onClick={() => {
                    ReactGA.event({ category: "Landing Page", action: "Click Login", label: "Footer" });
                  }} className="inline-flex items-center justify-center h-14 px-10 text-lg font-medium text-neutral-900 transition-colors bg-white border border-neutral-300 rounded-full hover:bg-neutral-50 font-geist shadow-xl w-full sm:w-auto">
                    Login
                  </button>
                </Link>
                <Link to="/signup">
                  <button onClick={() => {
                    ReactGA.event({ category: "Landing Page", action: "Click Signup", label: "Footer" });
                  }} className="inline-flex items-center justify-center h-14 px-10 text-lg font-medium text-white transition-colors bg-neutral-900 rounded-full hover:bg-neutral-800 font-geist shadow-xl w-full sm:w-auto">
                    Sign Up
                  </button>
                </Link>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
                <div className="w-full sm:w-auto">
                  <button onClick={() => {
                    ReactGA.event({ category: "Landing Page", action: "Go to Dashboard", label: "Footer" });
                    const route = getDashboardRoute();
                    navigate(route);
                  }} className="inline-flex items-center justify-center h-14 px-10 text-lg font-medium text-white transition-colors bg-neutral-900 rounded-full hover:bg-neutral-800 font-geist shadow-xl w-full sm:w-auto">
                    Go to Dashboard
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

      </main>

      {isEarlyAccessOpen && (
        <Suspense fallback={<div>Loading...</div>}>
          <EarlyAccessDialog
            isEarlyAccessOpen={isEarlyAccessOpen}
            setIsEarlyAccessOpen={setIsEarlyAccessOpen}
          />
        </Suspense>
      )}

      {/* Footer */}
      <footer className="bg-white pt-24 pb-12 border-t border-neutral-200" id="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">

          {/* Top Level: Branding */}
          <div className="mb-16">
            <Link to="/" className="flex items-center gap-2 group">
              <img src={artieImage} alt="Artie" className="w-40 h-40 object-contain" width="160" height="160" loading="lazy" />
            </Link>
          </div>

          {/* Middle Level: 4-Column Grid */}
          <div className="flex flex-col md:flex-row justify-between gap-12 lg:gap-8 mb-16">

            {/* Left Group: Navigation Links */}
            <div className="flex flex-col md:flex-row gap-12 lg:gap-8">
              {/* Column 1: Legal */}
              <div>
                <h4 className="font-bold text-neutral-900 mb-6 font-geist">Legal</h4>
                <ul className="space-y-4 text-sm text-neutral-500 font-geist">
                  <li><Link to="/privacy" onClick={() => ReactGA.event({ category: "Footer", action: "Clicked Privacy Link", label: "Privacy" })} className="hover:text-neutral-900 transition-colors">Privacy</Link></li>
                  <li><Link to="/terms" onClick={() => ReactGA.event({ category: "Footer", action: "Clicked Terms Link", label: "Terms" })} className="hover:text-neutral-900 transition-colors">Terms</Link></li>
                </ul>
              </div>

              {/* Column 2: Company */}
              <div>
                <h4 className="font-bold text-neutral-900 mb-6 font-geist">Company</h4>
                <ul className="space-y-4 text-sm text-neutral-500 font-geist">
                  <li><Link to="/careers" onClick={() => ReactGA.event({ category: "Footer", action: "Clicked Careers Link", label: "Careers" })} className="hover:text-neutral-900 transition-colors">Careers</Link></li>
                  <li><Link to="/investors" onClick={() => ReactGA.event({ category: "Footer", action: "Clicked Investors Link", label: "Investors" })} className="hover:text-neutral-900 transition-colors">Investors</Link></li>
                </ul>
              </div>

              {/* Column 3: Contact */}
              <div>
                <h4 className="font-bold text-neutral-900 mb-6 font-geist">Contact</h4>
                <div className="space-y-6 text-sm text-neutral-500 font-geist">
                  <div>
                    <p className="font-medium text-neutral-900 mb-2">Media</p>
                    <ul className="space-y-1">
                      <li><a href="mailto:lina@frankpublicity.com" className="hover:text-neutral-900 transition-colors">lina@frankpublicity.com</a></li>
                      <li><a href="mailto:clareanne@frankpublicity.com" className="hover:text-neutral-900 transition-colors">clareanne@frankpublicity.com</a></li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900 mb-2">General</p>
                    <a href="mailto:hello@artie.co" className="hover:text-neutral-900 transition-colors">hello@artie.co</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Group: Social */}
            <div>
              <h4 className="font-bold text-neutral-900 mb-6 font-geist">Social</h4>
              <div className="flex gap-4">
                <a href="https://www.instagram.com/officialartieco?igsh=ZWQyYXNwNG8zN3lr" onClick={() => ReactGA.event({ category: "Footer", action: "Clicked Instagram", label: "Instagram" })} target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-neutral-900 transition-colors">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="https://www.linkedin.com/company/artieco/" onClick={() => ReactGA.event({ category: "Footer", action: "Clicked Linkedin", label: "LinkedIn" })} target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-neutral-900 transition-colors">
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>

          </div>

          {/* Bottom Level: Separator & Copyright */}
          <div className="pt-8 border-t border-neutral-100">
            <p className="text-sm text-neutral-400 font-geist">
              © 2026 Artie. All rights reserved.
            </p>
          </div>

        </div>
      </footer>
    </div>
  );
};

export default LandingPage;